import { SECONDS } from "./time.ts";
import type { Cancellation, Unsubscribe } from "./types/async.ts";

export const DEFAULT_EVENT_BLOCK_MS = 1 * SECONDS;
export const TIME_TO_DEBOUNCE = 150;

export type YieldConfig<T> = {
  cancellation?: Cancellation;
  maxOccupancyMs?: number;
  onSleep?: (value: T, index: number) => void;
  yieldMs?: number;
};

export const sleep = (
  millis: number
): Promise<void> & { cancel: Unsubscribe } => {
  let resolve: () => void;
  let timeout: NodeJS.Timeout;
  const promise = new Promise<void>((r) => {
    resolve = r;
    timeout = setTimeout(r, millis);
  });
  const cancel = () => {
    clearTimeout(timeout);
    resolve();
  };

  return Object.assign(promise, { cancel });
};

/** Runs a loop, but yields the event loop if too much time has elapsed */
export const eventYieldingFor = async <T>(
  iterable: Iterable<T>,
  inner: (value: T) => Promise<void> | void,
  options?: YieldConfig<T>
) => {
  let last = Date.now();
  let ix = 0;
  for (const value of iterable) {
    if (
      Date.now() - last >
      (options?.maxOccupancyMs ?? DEFAULT_EVENT_BLOCK_MS)
    ) {
      if (options?.cancellation?.isCancelled) return;
      options?.onSleep?.(value, ix);
      // Need sleep of at least 1 ms to actually yield, otherwise the timer
      // resolves immediately
      await sleep(options?.yieldMs ?? 1);
      last = Date.now();
    }
    const result = inner(value);
    if (typeof result === "object" && "then" in result) {
      await result;
    }
    ix++;
  }
};
